import { Bot } from 'lucide-react';

import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SortableHeader } from '@/components/ui/data-table.tsx';

import getAvatarColorClass from '@/lib/avatarColor';
import { renderAgentStatusBadge } from '@/lib/badge';
import { formatDate } from '@/lib/date';

import { RowAction } from './row-action.tsx';

import type { Agent } from '@/types/agent.type.ts';
import type { ColumnDef } from '@tanstack/react-table';

export const getColumns = (): ColumnDef<Agent>[] => [
  {
    id: 'agent_name',
    accessorKey: 'agent_name',
    header: ({ column }) => <SortableHeader label="Agent Name" column={column} />,
    cell: ({ row }) => (
      <div className="flex items-center gap-3">
        <Avatar className="h-8 w-8">
          <AvatarFallback className="bg-primary text-primary-foreground">
            <Bot className="h-4 w-4" />
          </AvatarFallback>
        </Avatar>
        <div>
          <span className="font-medium">{row.original.agent_name}</span>
        </div>
      </div>
    ),
  },
  {
    id: 'canonical_name',
    accessorKey: 'canonical_name',
    header: ({ column }) => <SortableHeader label="Agent ID" column={column} />,
    cell: ({ getValue }) => <span className="text-sm text-foreground">{getValue() as string}</span>,
  },
  {
    id: 'voice',
    header: 'Voice',
    cell: ({ row }) => {
      const agent = row.original;
      return (
        <div className="flex items-center gap-2">
          <Avatar className="h-8 w-8">
            <AvatarFallback className={getAvatarColorClass(agent.voice.voice_name)}>
              {agent.voice.voice_name.charAt(0)}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="font-medium text-sm">{agent.voice.voice_name}</div>
            <div className="text-xs text-muted-foreground">
              {agent.voice.gender}, {agent.voice.language}
            </div>
          </div>
        </div>
      );
    },
  },
  {
    id: 'status',
    header: ({ column }) => <SortableHeader label="Status" column={column} />,
    accessorKey: 'status',
    cell: ({ getValue }) => renderAgentStatusBadge(getValue() as string),
  },
  {
    id: 'created_at',
    accessorKey: 'created_at',
    header: ({ column }) => <SortableHeader label="Created at" column={column} />,
    cell: ({ getValue }) => <span className="text-sm">{formatDate(getValue() as string)}</span>,
  },
  {
    id: 'actions',
    enableSorting: false,
    cell: ({ row }) => <RowAction agent={row.original} />,
  },
];
